/**
 * Member Table Component
 * 
 * Table component for displaying members with pagination.
 */

import * as React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import type { Member } from "@/services/userService";
import { MemberTableRow } from "./MemberTableRow";

interface MemberTableProps {
  members: Member[];
  loading: boolean;
  error: string | null;
  hasNext: boolean;
  hasPrevious: boolean;
  nextCursor: string | null;
  previousCursor: string | null;
  onNextPage: () => void;
  onPreviousPage: () => void;
  syncingUserId: string | null;
  onSyncStart: (userId: string) => void;
  onSyncEnd: () => void;
  onResetPassword: (userId: string, iar: string, name: string, callsign: string) => void;
  onSetEmail: (userId: string, name: string, callsign: string, currentEmail: string) => void;
  onRefresh: () => void;
  sortBy: "nama_lengkap" | "callsign" | "tanggal_terbit" | "masa_laku" | "provinsi" | "status_callsign" | "status_keanggotaan" | "_id";
  sortOrder: "asc" | "desc";
  onSortChange: (field: "nama_lengkap" | "callsign" | "tanggal_terbit" | "masa_laku" | "provinsi" | "status_callsign" | "status_keanggotaan" | "_id", order: "asc" | "desc") => void;
}

export function MemberTable({
  members,
  loading,
  error,
  hasNext,
  hasPrevious,
  onNextPage,
  onPreviousPage,
  syncingUserId,
  onSyncStart,
  onSyncEnd,
  onResetPassword,
  onSetEmail,
  onRefresh,
  sortBy,
  sortOrder,
  onSortChange,
}: MemberTableProps) {
  const handleSort = (field: "nama_lengkap" | "callsign" | "tanggal_terbit" | "masa_laku" | "provinsi" | "status_callsign" | "status_keanggotaan" | "_id") => {
    const newOrder = sortBy === field && sortOrder === "asc" ? "desc" : "asc";
    onSortChange(field, newOrder);
  };

  const SortIcon = ({ field }: { field: "nama_lengkap" | "callsign" | "tanggal_terbit" | "masa_laku" | "provinsi" | "status_callsign" | "status_keanggotaan" | "_id" }) => {
    if (sortBy !== field) {
      return <ArrowUpDown className="ml-1.5 h-3 w-3 text-muted-foreground" />;
    }
    return sortOrder === "asc" ? (
      <ArrowUp className="ml-1.5 h-3 w-3" />
    ) : (
      <ArrowDown className="ml-1.5 h-3 w-3" />
    );
  };

  return (
    <div className="space-y-3">
      {/* Toolbar: Count */}
      <div className="flex items-center justify-end">
        <span className="text-xs text-muted-foreground">
          {loading ? "Memuat..." : `${members.length} anggota`}
        </span>
      </div>

      {/* Error */}
      {error && (
        <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      {/* Table */}
      {loading && members.length === 0 ? (
        <div className="rounded-lg border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[120px]">ID / Callsign</TableHead>
                <TableHead className="min-w-[130px]">Nama</TableHead>
                <TableHead className="hidden md:table-cell">Kontak</TableHead>
                <TableHead className="hidden lg:table-cell">Login</TableHead>
                <TableHead>Masa Laku</TableHead>
                <TableHead className="hidden sm:table-cell">Area</TableHead>
                <TableHead className="hidden sm:table-cell">IAR</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[50px] sticky right-0 bg-background z-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: 8 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 9 }).map((_, j) => (
                    <TableCell key={j} className={j === 8 ? "sticky right-0 bg-background z-10" : ""}>
                      <div className="h-4 bg-muted rounded animate-pulse" style={{ width: j === 0 ? "80px" : j === 1 ? "120px" : j === 8 ? "28px" : "60%", animationDelay: `${(i * 9 + j) * 30}ms` }} />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : members.length === 0 ? (
        <div className="rounded-lg border p-12 text-center">
          <p className="text-muted-foreground">Tidak ada data anggota</p>
        </div>
      ) : (
        <div className="rounded-lg border overflow-x-auto relative">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[120px] sm:min-w-[150px]">
                  <Button
                    variant="ghost"
                    className="h-7 px-1.5 hover:bg-transparent text-xs font-medium"
                    onClick={() => handleSort("_id")}
                  >
                    <span className="hidden sm:inline">ID Client /</span> Callsign
                    <SortIcon field="_id" />
                  </Button>
                </TableHead>
                <TableHead className="min-w-[130px] sm:min-w-[150px]">
                  <Button
                    variant="ghost"
                    className="h-7 px-1.5 hover:bg-transparent text-xs font-medium"
                    onClick={() => handleSort("nama_lengkap")}
                  >
                    Nama
                    <SortIcon field="nama_lengkap" />
                  </Button>
                </TableHead>
                <TableHead className="hidden md:table-cell min-w-[150px] text-xs font-medium">Kontak</TableHead>
                <TableHead className="hidden lg:table-cell min-w-[100px] text-xs font-medium">
                  <span className="block leading-tight">Login</span>
                  <span className="block text-[10px] font-normal text-muted-foreground">atau KTP</span>
                </TableHead>
                <TableHead className="min-w-[120px] sm:min-w-[130px]">
                  <Button
                    variant="ghost"
                    className="h-7 px-1.5 hover:bg-transparent text-xs font-medium"
                    onClick={() => handleSort("masa_laku")}
                  >
                    <span className="hidden sm:inline">Masa Laku</span>
                    <span className="inline sm:hidden">Berlaku</span>
                    <SortIcon field="masa_laku" />
                  </Button>
                </TableHead>
                <TableHead className="hidden sm:table-cell min-w-[120px]">
                  <Button
                    variant="ghost"
                    className="h-7 px-1.5 hover:bg-transparent text-xs font-medium"
                    onClick={() => handleSort("provinsi")}
                  >
                    Area
                    <SortIcon field="provinsi" />
                  </Button>
                </TableHead>
                <TableHead className="hidden sm:table-cell min-w-[90px]">
                  <Button
                    variant="ghost"
                    className="h-7 px-1.5 hover:bg-transparent text-xs font-medium"
                    onClick={() => handleSort("status_callsign")}
                  >
                    IAR
                    <SortIcon field="status_callsign" />
                  </Button>
                </TableHead>
                <TableHead className="min-w-[110px] sm:min-w-[130px]">
                  <Button
                    variant="ghost"
                    className="h-7 px-1.5 hover:bg-transparent text-xs font-medium"
                    onClick={() => handleSort("status_keanggotaan")}
                  >
                    <span className="hidden sm:inline">Keanggotaan</span>
                    <span className="inline sm:hidden">Status</span>
                    <SortIcon field="status_keanggotaan" />
                  </Button>
                </TableHead>
                <TableHead className="w-[50px] sticky right-0 bg-background z-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.map((member) => (
                <MemberTableRow
                  key={member._id}
                  member={member}
                  loading={loading}
                  syncingUserId={syncingUserId}
                  onSyncStart={onSyncStart}
                  onSyncEnd={onSyncEnd}
                  onResetPassword={onResetPassword}
                  onSetEmail={onSetEmail}
                  onRefresh={onRefresh}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="text-xs text-muted-foreground">
          {members.length > 0 && `Menampilkan ${members.length} anggota`}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onPreviousPage}
            disabled={!hasPrevious || loading}
            className="text-xs h-8"
          >
            <ChevronLeft className="h-3.5 w-3.5 mr-1" />
            Sebelumnya
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onNextPage}
            disabled={!hasNext || loading}
            className="text-xs h-8"
          >
            Berikutnya
            <ChevronRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}
